import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemText,
  makeStyles,
  Menu,
  MenuItem,
  Typography
} from '@material-ui/core';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import { useCurrentUser } from 'src/store/account/selectors';
import {
  getNotifications,
  getNotificationsNewCount,
  resolveNotification
} from 'src/api/providers';

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    width: 360,
    maxHeight: 420
  },
  headerWrap: {
    padding: theme.spacing(1.5, 2, 1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  header: {
    fontWeight: 700
  },
  count: {
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  item: {
    whiteSpace: 'normal',
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1.2),
    paddingBottom: theme.spacing(1.1)
  },
  itemBody: {
    width: '100%'
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%'
  },
  itemFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(0.5)
  },
  detail: {
    color: theme.palette.text.secondary,
    fontSize: 12
  },
  typeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.3, 0.9),
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.2,
    border: '1px solid transparent'
  },
  typeAlert: {
    backgroundColor: '#FFF8E1',
    color: '#8A6D1A',
    borderColor: '#F3D36B'
  },
  typeAnnouncement: {
    backgroundColor: '#E8F3FF',
    color: '#1D4E89',
    borderColor: '#9DC2EE'
  },
  typeReminder: {
    backgroundColor: '#E8F8EF',
    color: '#1E6A3A',
    borderColor: '#9CD7B2'
  },
  typeInformative: {
    backgroundColor: '#F1F3F5',
    color: '#4B5563',
    borderColor: '#D0D7DE'
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    marginTop: 7,
    marginRight: theme.spacing(1.25),
    flexShrink: 0
  },
  footer: {
    padding: theme.spacing(1.25, 1.5, 1.5)
  },
  footerButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  fullButton: {
    justifyContent: 'space-between',
    textTransform: 'none',
    fontWeight: 600
  },
  errorWrap: {
    padding: theme.spacing(1.5, 2)
  },
  retry: {
    marginTop: theme.spacing(1),
    cursor: 'pointer'
  },
  loaderWrap: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center'
  },
  resolveLink: {
    textTransform: 'none',
    fontSize: 11,
    fontWeight: 600,
    minWidth: 'auto',
    padding: 0,
    lineHeight: 1,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline'
    }
  }
}));

const NotificationsMenu = () => {
  const classes = useStyles();
  const history = useHistory();
  const currentUser = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const open = Boolean(anchorEl);

  const canResolveNotifications = Boolean(
    currentUser?.groups?.some((group) =>
      ['admin', 'network_manager'].includes(group.name)
    )
  );

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getNotifications({
        limit: 5,
        offset: 0,
        order: 'desc',
        orderBy: 'created_date'
      });

      setNotifications(data?.results || []);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'No fue posible cargar notificaciones';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNewCount = useCallback(async () => {
    try {
      const data = await getNotificationsNewCount();
      setNewCount(data?.new_count || 0);
    } catch (err) {
      setNewCount(0);
    }
  }, []);

  useEffect(() => {
    fetchNewCount();
  }, [fetchNewCount]);

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    await fetchNotifications();
    await fetchNewCount();
  };

  const handleClose = () => setAnchorEl(null);

  const normalizeInternalPath = (link) => {
    if (!link) return '';
    if (/^https?:\/\//i.test(link)) {
      try {
        const url = new URL(link);
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (e) {
        return link;
      }
    }
    return link.startsWith('/') ? link : `/${link}`;
  };

  const handleClickNotification = (item) => {
    if (item?.link) history.push(normalizeInternalPath(item.link));
    handleClose();
  };

  const handleResolveNotification = async (event, item) => {
    event.stopPropagation();
    event.preventDefault();

    try {
      await resolveNotification(item.id);
      await fetchNotifications();
      await fetchNewCount();
      enqueueSnackbar('Notificación marcada como resuelta.', {
        variant: 'success'
      });
      if (history.location.pathname === '/notifications') {
        window.location.reload();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'No fue posible marcar la notificación como resuelta';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleGoToFullList = () => {
    handleClose();
    history.push('/notifications');
  };

  const getTypeClassName = (type) => {
    switch ((type || '').toUpperCase()) {
      case 'ALERTA':
        return classes.typeAlert;
      case 'ANUNCIO':
        return classes.typeAnnouncement;
      case 'RECORDATORIO':
        return classes.typeReminder;
      case 'INFORMATIVA':
        return classes.typeInformative;
      default:
        return classes.typeInformative;
    }
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen}>
        <Badge
          badgeContent={newCount || 0}
          color="secondary"
          max={99}
          invisible={newCount === 0}
        >
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        keepMounted
        PaperProps={{ className: classes.menuPaper }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box className={classes.headerWrap}>
          <Typography variant="subtitle1" className={classes.header}>
            Notificaciones
          </Typography>
          <Typography className={classes.count}>
            Nuevas: {newCount || 0}
          </Typography>
        </Box>

        <Divider />

        {loading ? (
          <Box className={classes.loaderWrap}>
            <CircularProgress size={20} />
          </Box>
        ) : null}

        {!loading && error ? (
          <Box className={classes.errorWrap}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
            <Typography
              className={classes.retry}
              variant="body2"
              color="primary"
              onClick={fetchNotifications}
            >
              Reintentar
            </Typography>
          </Box>
        ) : null}

        {!loading &&
          !error &&
          notifications.map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => handleClickNotification(item)}
              className={classes.item}
            >
              <Box className={classes.itemBody}>
                <Box className={classes.itemHeader}>
                  {item.is_new ? <span className={classes.dot} /> : null}
                  <ListItemText
                    primary={
                      <span
                        className={clsx(
                          classes.typeBadge,
                          getTypeClassName(item.notification_types)
                        )}
                      >
                        {item.notification_types || 'Informativa'}
                      </span>
                    }
                    secondary={
                      <span className={classes.detail}>{item.detail}</span>
                    }
                  />
                </Box>

                {canResolveNotifications &&
                (item.notification_types || '').toUpperCase() === 'ALERTA' &&
                !item.is_resolved ? (
                  <Box className={classes.itemFooter}>
                    <Button
                      size="small"
                      className={classes.resolveLink}
                      onClick={(event) =>
                        handleResolveNotification(event, item)
                      }
                    >
                      Resolver
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </MenuItem>
          ))}

        {!loading && !error ? <Divider /> : null}

        {!loading && !error ? (
          <Box className={classes.footer}>
            <Button
              fullWidth
              color="primary"
              onClick={handleGoToFullList}
              className={classes.fullButton}
            >
              Ver todas las notificaciones
            </Button>
          </Box>
        ) : null}
      </Menu>
    </>
  );
};

export default NotificationsMenu;
